import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  Search,
  Loader2,
  Plus,
  Edit,
  Eye,
  Trash2,
  DollarSign,
  TrendingUp,
  UserPlus,
  Percent,
  Facebook,
  Store,
  Calendar,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Copy,
  Check,
  AlertTriangle,
  Send,
  KeyRound,
} from "lucide-react";

const PartnerManagement = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerStats, setPartnerStats] = useState(null);

  // Form states
  const [createForm, setCreateForm] = useState({
    user_email: "",
    partner_type: "admin_groupe",
    promo_code: "",
    facebook_group_name: "",
    facebook_group_url: "",
    company_name: "",
    commission_rate_first: 25,
    commission_rate_recurring: 5,
    discount_rate_first: 25,
    notes: "",
  });
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  // Applications states
  const [activeTab, setActiveTab] = useState("partners");
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [applicationsPage, setApplicationsPage] = useState(1);
  const [applicationsTotalPages, setApplicationsTotalPages] = useState(1);
  const [applicationsTotal, setApplicationsTotal] = useState(0);
  const [applicationsFilter, setApplicationsFilter] = useState("pending");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [approvePromoCode, setApprovePromoCode] = useState("");
  const [approving, setApproving] = useState(false);

  // Commissions states
  const [commissions, setCommissions] = useState([]);
  const [loadingCommissions, setLoadingCommissions] = useState(false);
  const [commissionsPage, setCommissionsPage] = useState(1);
  const [commissionsTotalPages, setCommissionsTotalPages] = useState(1);
  const [commissionsFilter, setCommissionsFilter] = useState("pending");
  const [commissionsSummary, setCommissionsSummary] = useState({});
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [resendingPassword, setResendingPassword] = useState(null);

  // Fetch partners
  const fetchPartners = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (typeFilter !== "all") params.append("partner_type", typeFilter);
      if (searchTerm) params.append("search", searchTerm);
      params.append("page", page);
      params.append("per_page", 10);

      const response = await axios.get(`${API}/partners?${params}`);
      setPartners(response.data.partners || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching partners:", error);
      toast.error("Erreur lors du chargement des partenaires");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, searchTerm, page]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    try {
      setLoadingApplications(true);
      const params = new URLSearchParams();
      if (applicationsFilter !== "all") params.append("status", applicationsFilter);
      params.append("page", applicationsPage);
      params.append("per_page", 10);

      const response = await axios.get(`${API}/partners/applications?${params}`);
      setApplications(response.data.applications || []);
      setApplicationsTotalPages(response.data.total_pages || 1);
      setApplicationsTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Erreur lors du chargement des candidatures");
    } finally {
      setLoadingApplications(false);
    }
  }, [applicationsFilter, applicationsPage]);

  useEffect(() => {
    if (activeTab === "applications") {
      fetchApplications();
    }
  }, [activeTab, fetchApplications]);

  // Approve application
  const handleApproveApplication = async () => {
    if (!selectedApplication || !approvePromoCode.trim()) {
      toast.error("Code promo requis pour approuver");
      return;
    }

    try {
      setApproving(true);
      await axios.put(
        `${API}/partners/applications/${selectedApplication.id}?status=approved&promo_code=${encodeURIComponent(approvePromoCode.trim().toUpperCase())}`
      );
      toast.success("Candidature approuvée! Le partenaire a été créé.");
      setShowApproveModal(false);
      setSelectedApplication(null);
      setApprovePromoCode("");
      fetchApplications();
      fetchPartners(); // Refresh partners list too
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'approbation");
    } finally {
      setApproving(false);
    }
  };

  // Reject application
  const handleRejectApplication = async (applicationId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir rejeter cette candidature?")) {
      return;
    }

    try {
      await axios.put(`${API}/partners/applications/${applicationId}?status=rejected`);
      toast.success("Candidature rejetée");
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du rejet");
    }
  };

  // Fetch all commissions
  const fetchCommissions = useCallback(async () => {
    try {
      setLoadingCommissions(true);
      const params = new URLSearchParams();
      if (commissionsFilter !== "all") params.append("status", commissionsFilter);
      params.append("page", commissionsPage);
      params.append("per_page", 20);

      const response = await axios.get(`${API}/partners/all-commissions?${params}`);
      setCommissions(response.data.commissions || []);
      setCommissionsTotalPages(response.data.total_pages || 1);
      setCommissionsSummary(response.data.summary || {});
    } catch (error) {
      console.error("Error fetching commissions:", error);
      toast.error("Erreur lors du chargement des commissions");
    } finally {
      setLoadingCommissions(false);
    }
  }, [commissionsFilter, commissionsPage]);

  useEffect(() => {
    if (activeTab === "commissions") {
      fetchCommissions();
    }
  }, [activeTab, fetchCommissions]);

  // Toggle commission selection
  const toggleCommissionSelection = (commissionId) => {
    setSelectedCommissions(prev => 
      prev.includes(commissionId)
        ? prev.filter(id => id !== commissionId)
        : [...prev, commissionId]
    );
  };

  // Select all pending commissions
  const toggleSelectAll = () => {
    const pendingIds = commissions.filter(c => c.status === "pending").map(c => c.id);
    if (selectedCommissions.length === pendingIds.length) {
      setSelectedCommissions([]);
    } else {
      setSelectedCommissions(pendingIds);
    }
  };

  // Mark selected commissions as paid
  const handleMarkPaid = async () => {
    if (selectedCommissions.length === 0) {
      toast.error("Sélectionnez des commissions à payer");
      return;
    }

    if (!window.confirm(`Marquer ${selectedCommissions.length} commission(s) comme payée(s)?`)) {
      return;
    }

    try {
      setMarkingPaid(true);
      await axios.post(`${API}/partners/commissions/mark-paid-batch`, selectedCommissions);
      toast.success(`${selectedCommissions.length} commission(s) marquée(s) comme payée(s)`);
      setSelectedCommissions([]);
      fetchCommissions();
      fetchPartners(); // Refresh partner stats too
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du paiement");
    } finally {
      setMarkingPaid(false);
    }
  };

  // Fetch partner details
  const fetchPartnerDetails = async (partnerId) => {
    try {
      const response = await axios.get(`${API}/partners/${partnerId}`);
      setSelectedPartner(response.data.partner);
      setPartnerStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching partner details:", error);
      toast.error("Erreur lors du chargement des détails");
    }
  };

  // Resend password setup link
  const resendPasswordSetup = async (partnerId, method = "both") => {
    try {
      setResendingPassword(partnerId);
      const response = await axios.post(`${API}/partners/${partnerId}/resend-password-setup?method=${method}`);
      const results = response.data.results;
      
      let message = "Lien envoyé: ";
      if (results.sms === "sent") message += "SMS ✓ ";
      if (results.sms === "no_phone") message += "(pas de téléphone) ";
      if (results.email === "sent") message += "Email ✓";
      
      toast.success(message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setResendingPassword(null);
    }
  };

  // Create partner
  const handleCreate = async () => {
    if (!createForm.user_email || !createForm.promo_code) {
      toast.error("L'email et le code promo sont requis");
      return;
    }

    try {
      setCreating(true);
      await axios.post(`${API}/partners`, createForm);
      toast.success("Partenaire créé avec succès!");
      setShowCreateModal(false);
      setCreateForm({
        user_email: "",
        partner_type: "admin_groupe",
        promo_code: "",
        facebook_group_name: "",
        facebook_group_url: "",
        company_name: "",
        commission_rate_first: 25,
        commission_rate_recurring: 5,
        discount_rate_first: 25,
        notes: "",
      });
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  // Update partner status
  const handleStatusChange = async (partnerId, newStatus) => {
    try {
      await axios.put(`${API}/partners/${partnerId}`, { status: newStatus });
      toast.success("Statut mis à jour");
      fetchPartners();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Copy promo code
  const copyPromoCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const variants = {
      actif: "bg-green-100 text-green-800 border-green-200",
      suspendu: "bg-yellow-100 text-yellow-800 border-yellow-200",
      inactif: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return (
      <Badge className={`${variants[status] || variants.inactif} border`}>
        {status === "actif" ? "Actif" : status === "suspendu" ? "Suspendu" : "Inactif"}
      </Badge>
    );
  };

  // Render partner type badge
  const renderTypeBadge = (type) => {
    if (type === "admin_groupe") {
      return (
        <Badge className="bg-blue-100 text-blue-800 border-blue-200 border flex items-center gap-1">
          <Facebook className="h-3 w-3" />
          Admin Groupe
        </Badge>
      );
    }
    return (
      <Badge className="bg-purple-100 text-purple-800 border-purple-200 border flex items-center gap-1">
        <Store className="h-3 w-3" />
        Revendeur
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="partner-management">
      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="partners" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Partenaires ({total})
          </TabsTrigger>
          <TabsTrigger value="applications" className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Candidatures
            {applicationsTotal > 0 && applicationsFilter === "pending" && (
              <Badge className="ml-1 bg-orange-500 text-white h-5 w-5 p-0 flex items-center justify-center text-xs">
                {applicationsTotal}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="commissions" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Commissions
            {commissionsSummary.count_pending > 0 && (
              <Badge className="ml-1 bg-green-500 text-white h-5 w-5 p-0 flex items-center justify-center text-xs">
                {commissionsSummary.count_pending}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Partners Tab */}
        <TabsContent value="partners" className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Users className="h-5 w-5 text-brand-600" />
                Gestion des Partenaires
              </h2>
              <p className="text-sm text-gray-500">
                Gérez les admins de groupe Facebook et les revendeurs
              </p>
            </div>
            <Button onClick={() => setShowCreateModal(true)} className="bg-brand-600 hover:bg-brand-700">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau Partenaire
            </Button>
          </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-xl font-semibold">{total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Actifs</p>
                <p className="text-xl font-semibold">
                  {partners.filter(p => p.status === "actif").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Filleuls Total</p>
                <p className="text-xl font-semibold">
                  {partners.reduce((acc, p) => acc + (p.total_referrals || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Commissions</p>
                <p className="text-xl font-semibold">
                  ${partners.reduce((acc, p) => acc + (p.total_commissions_earned || 0), 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Rechercher par code promo, groupe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="actif">Actif</SelectItem>
            <SelectItem value="suspendu">Suspendu</SelectItem>
            <SelectItem value="inactif">Inactif</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="admin_groupe">Admin Groupe</SelectItem>
            <SelectItem value="revendeur">Revendeur</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchPartners}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Partners Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun partenaire trouvé</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partenaire</TableHead>
                  <TableHead>Code Promo</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Filleuls</TableHead>
                  <TableHead>Commissions</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">
                          {partner.facebook_group_name || partner.company_name || partner.user?.name || "—"}
                        </p>
                        <p className="text-xs text-gray-500">{partner.user?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                          {partner.promo_code}
                        </code>
                        <button
                          onClick={() => copyPromoCode(partner.promo_code)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {copiedCode === partner.promo_code ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>{renderTypeBadge(partner.partner_type)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <UserPlus className="h-4 w-4 text-gray-400" />
                        <span>{partner.active_referrals || 0}</span>
                        <span className="text-gray-400">/ {partner.total_referrals || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">${(partner.total_commissions_earned || 0).toFixed(2)}</p>
                        {partner.commissions_pending > 0 && (
                          <p className="text-xs text-yellow-600">
                            ${partner.commissions_pending.toFixed(2)} en attente
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{renderStatusBadge(partner.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            fetchPartnerDetails(partner.id);
                            setShowDetailModal(true);
                          }}
                          title="Voir détails"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => resendPasswordSetup(partner.id, "sms")}
                          disabled={resendingPassword === partner.id}
                          title="Renvoyer lien mot de passe (SMS)"
                        >
                          {resendingPassword === partner.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <KeyRound className="h-4 w-4 text-amber-600" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedPartner(partner);
                            setShowEditModal(true);
                          }}
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-gray-500">
            Page {page} sur {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
        </TabsContent>

        {/* Applications Tab */}
        <TabsContent value="applications" className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-brand-600" />
                Candidatures Partenaires
              </h2>
              <p className="text-sm text-gray-500">
                Gérez les demandes de partenariat reçues via le site
              </p>
            </div>
            <div className="flex gap-2">
              <Select value={applicationsFilter} onValueChange={setApplicationsFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="approved">Approuvées</SelectItem>
                  <SelectItem value="rejected">Rejetées</SelectItem>
                  <SelectItem value="all">Toutes</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchApplications}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              {loadingApplications ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune candidature {applicationsFilter === "pending" ? "en attente" : ""}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Candidat</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Groupe/Entreprise</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{app.first_name} {app.last_name}</p>
                            <p className="text-xs text-gray-500">{app.email}</p>
                            {app.phone && <p className="text-xs text-gray-400">{app.phone}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            {app.partner_type === "admin_groupe" ? (
                              <><Facebook className="h-3 w-3 text-blue-600" /> Admin Groupe</>
                            ) : (
                              <><Store className="h-3 w-3 text-purple-600" /> Revendeur</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px]">
                            <p className="truncate font-medium">
                              {app.facebook_group_name || app.company_name || "—"}
                            </p>
                            {app.facebook_group_url && (
                              <a
                                href={app.facebook_group_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:underline truncate block"
                              >
                                {app.facebook_group_url}
                              </a>
                            )}
                            {app.group_member_count && (
                              <p className="text-xs text-gray-400">{app.group_member_count} membres</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-gray-500 text-sm">
                            <Calendar className="h-3 w-3" />
                            {new Date(app.created_at).toLocaleDateString("fr-CA")}
                          </div>
                        </TableCell>
                        <TableCell>
                          {app.status === "pending" && (
                            <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                              En attente
                            </Badge>
                          )}
                          {app.status === "approved" && (
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              Approuvée
                            </Badge>
                          )}
                          {app.status === "rejected" && (
                            <Badge className="bg-red-100 text-red-800 border-red-200">
                              Rejetée
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {app.status === "pending" && (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => {
                                  setSelectedApplication(app);
                                  // Generate a promo code suggestion
                                  const suggestedCode = app.facebook_group_name
                                    ? app.facebook_group_name
                                        .split(" ")
                                        .slice(0, 3)
                                        .join("-")
                                        .toUpperCase()
                                        .replace(/[^A-Z0-9-]/g, "")
                                    : app.company_name
                                    ? app.company_name.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10)
                                    : `PARTNER-${app.id.split("-")[1]}`;
                                  setApprovePromoCode(suggestedCode);
                                  setShowApproveModal(true);
                                }}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Approuver
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleRejectApplication(app.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          {app.status !== "pending" && (
                            <span className="text-xs text-gray-400">
                              {app.status === "approved" ? "Traité" : "Refusé"}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Applications Pagination */}
          {applicationsTotalPages > 1 && (
            <div className="flex justify-center items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setApplicationsPage(p => Math.max(1, p - 1))}
                disabled={applicationsPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-500">
                Page {applicationsPage} sur {applicationsTotalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setApplicationsPage(p => Math.min(applicationsTotalPages, p + 1))}
                disabled={applicationsPage === applicationsTotalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions" className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                Gestion des Commissions
              </h2>
              <p className="text-sm text-gray-500">
                Suivez et payez les commissions des partenaires
              </p>
            </div>
            <div className="flex gap-2">
              <Select value={commissionsFilter} onValueChange={setCommissionsFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="paid">Payées</SelectItem>
                  <SelectItem value="all">Toutes</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchCommissions}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">En attente</p>
                <p className="text-2xl font-bold text-yellow-600">
                  ${commissionsSummary.total_pending?.toFixed(2) || "0.00"}
                </p>
                <p className="text-xs text-gray-400">
                  {commissionsSummary.count_pending || 0} commission(s)
                </p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">Payées</p>
                <p className="text-2xl font-bold text-green-600">
                  ${commissionsSummary.total_paid?.toFixed(2) || "0.00"}
                </p>
                <p className="text-xs text-gray-400">
                  {commissionsSummary.count_paid || 0} commission(s)
                </p>
              </CardContent>
            </Card>
            <Card className="col-span-2">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Sélectionnées</p>
                    <p className="text-xl font-bold">
                      {selectedCommissions.length} commission(s)
                    </p>
                  </div>
                  <Button
                    onClick={handleMarkPaid}
                    disabled={selectedCommissions.length === 0 || markingPaid}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {markingPaid ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Marquer comme payées
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-0">
              {loadingCommissions ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
                </div>
              ) : commissions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune commission {commissionsFilter === "pending" ? "en attente" : ""}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={
                            commissions.filter(c => c.status === "pending").length > 0 &&
                            selectedCommissions.length === commissions.filter(c => c.status === "pending").length
                          }
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300"
                          disabled={commissions.filter(c => c.status === "pending").length === 0}
                        />
                      </TableHead>
                      <TableHead>Partenaire</TableHead>
                      <TableHead>Transaction</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissions.map((com) => (
                      <TableRow key={com.id} className={selectedCommissions.includes(com.id) ? "bg-green-50" : ""}>
                        <TableCell>
                          {com.status === "pending" && (
                            <input
                              type="checkbox"
                              checked={selectedCommissions.includes(com.id)}
                              onChange={() => toggleCommissionSelection(com.id)}
                              className="rounded border-gray-300"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{com.partner_name}</p>
                            <p className="text-xs text-gray-400 font-mono">{com.partner_promo_code}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs font-mono text-gray-500">{com.transaction_id?.slice(0, 12)}...</span>
                        </TableCell>
                        <TableCell>
                          ${com.transaction_amount?.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <span className="font-bold text-green-600">+${com.commission_amount?.toFixed(2)}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {com.is_first_transfer ? "1er transfert" : "Récurrent"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-gray-500 text-sm">
                            <Calendar className="h-3 w-3" />
                            {new Date(com.created_at).toLocaleDateString("fr-CA")}
                          </div>
                        </TableCell>
                        <TableCell>
                          {com.status === "pending" ? (
                            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                              En attente
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              <Check className="h-3 w-3 mr-1" />
                              Payée
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Commissions Pagination */}
          {commissionsTotalPages > 1 && (
            <div className="flex justify-center items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCommissionsPage(p => Math.max(1, p - 1))}
                disabled={commissionsPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-500">
                Page {commissionsPage} sur {commissionsTotalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCommissionsPage(p => Math.min(commissionsTotalPages, p + 1))}
                disabled={commissionsPage === commissionsTotalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Approve Application Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approuver la candidature</DialogTitle>
            <DialogDescription>
              Créez un code promo unique pour ce nouveau partenaire
            </DialogDescription>
          </DialogHeader>

          {selectedApplication && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p><strong>Nom:</strong> {selectedApplication.first_name} {selectedApplication.last_name}</p>
                <p><strong>Email:</strong> {selectedApplication.email}</p>
                <p><strong>Type:</strong> {selectedApplication.partner_type === "admin_groupe" ? "Admin Groupe Facebook" : "Revendeur"}</p>
                {selectedApplication.facebook_group_name && (
                  <p><strong>Groupe:</strong> {selectedApplication.facebook_group_name}</p>
                )}
                {selectedApplication.company_name && (
                  <p><strong>Entreprise:</strong> {selectedApplication.company_name}</p>
                )}
                {selectedApplication.message && (
                  <p className="text-sm text-gray-600"><strong>Message:</strong> {selectedApplication.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Code Promo *</Label>
                <Input
                  placeholder="CODE-PROMO"
                  value={approvePromoCode}
                  onChange={(e) => setApprovePromoCode(e.target.value.toUpperCase())}
                  className="uppercase font-mono"
                />
                <p className="text-xs text-gray-500">
                  Ce code sera utilisé par les membres du partenaire lors de leur inscription
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleApproveApplication}
              disabled={approving || !approvePromoCode.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {approving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Approbation...</>
              ) : (
                <><Check className="h-4 w-4 mr-2" /> Approuver et Créer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Partner Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouveau Partenaire</DialogTitle>
            <DialogDescription>
              Créez un compte partenaire pour un admin de groupe Facebook ou un revendeur
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email de l'utilisateur *</Label>
              <Input
                placeholder="email@exemple.com"
                value={createForm.user_email}
                onChange={(e) => setCreateForm(f => ({ ...f, user_email: e.target.value }))}
              />
              <p className="text-xs text-gray-500">L'utilisateur doit déjà avoir un compte Middleman</p>
            </div>

            <div className="space-y-2">
              <Label>Type de partenaire *</Label>
              <Select
                value={createForm.partner_type}
                onValueChange={(v) => setCreateForm(f => ({ ...f, partner_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin_groupe">Admin de Groupe Facebook</SelectItem>
                  <SelectItem value="revendeur">Revendeur</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Code Promo *</Label>
              <Input
                placeholder="GROUPE-MTL-HABS"
                value={createForm.promo_code}
                onChange={(e) => setCreateForm(f => ({ ...f, promo_code: e.target.value.toUpperCase() }))}
                className="uppercase"
              />
              <p className="text-xs text-gray-500">Code unique que les membres utiliseront lors de l'inscription</p>
            </div>

            {createForm.partner_type === "admin_groupe" ? (
              <>
                <div className="space-y-2">
                  <Label>Nom du groupe Facebook</Label>
                  <Input
                    placeholder="Fans des Canadiens - Revente Billets"
                    value={createForm.facebook_group_name}
                    onChange={(e) => setCreateForm(f => ({ ...f, facebook_group_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL du groupe</Label>
                  <Input
                    placeholder="https://facebook.com/groups/..."
                    value={createForm.facebook_group_url}
                    onChange={(e) => setCreateForm(f => ({ ...f, facebook_group_url: e.target.value }))}
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label>Nom de l'entreprise</Label>
                <Input
                  placeholder="Nom de l'entreprise"
                  value={createForm.company_name}
                  onChange={(e) => setCreateForm(f => ({ ...f, company_name: e.target.value }))}
                />
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Commission 1er transfert (%)</Label>
                <Input
                  type="number"
                  value={createForm.commission_rate_first}
                  onChange={(e) => setCreateForm(f => ({ ...f, commission_rate_first: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Commission récurrente (%)</Label>
                <Input
                  type="number"
                  value={createForm.commission_rate_recurring}
                  onChange={(e) => setCreateForm(f => ({ ...f, commission_rate_recurring: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Réduction membre (%)</Label>
                <Input
                  type="number"
                  value={createForm.discount_rate_first}
                  onChange={(e) => setCreateForm(f => ({ ...f, discount_rate_first: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes internes</Label>
              <Textarea
                placeholder="Notes sur ce partenaire..."
                value={createForm.notes}
                onChange={(e) => setCreateForm(f => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-brand-600 hover:bg-brand-700">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Créer le partenaire
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails du Partenaire</DialogTitle>
          </DialogHeader>

          {selectedPartner && partnerStats && (
            <div className="space-y-6">
              {/* Partner Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Nom</p>
                  <p className="font-medium">
                    {selectedPartner.facebook_group_name || selectedPartner.company_name || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Code Promo</p>
                  <code className="bg-gray-100 px-2 py-1 rounded font-mono">{selectedPartner.promo_code}</code>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  {renderTypeBadge(selectedPartner.partner_type)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Statut</p>
                  {renderStatusBadge(selectedPartner.status)}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-brand-600">{partnerStats.total_referrals}</p>
                    <p className="text-xs text-gray-500">Filleuls Total</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">${partnerStats.commissions_this_month.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Ce mois</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">${partnerStats.commissions_this_year.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Cette année</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-600">${partnerStats.commissions_pending.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">En attente</p>
                  </CardContent>
                </Card>
              </div>

              {/* Commission Rates */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Taux de Commission</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-lg font-bold text-brand-600">{selectedPartner.commission_rate_first}%</p>
                      <p className="text-xs text-gray-500">1er transfert</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-purple-600">{selectedPartner.commission_rate_recurring}%</p>
                      <p className="text-xs text-gray-500">Récurrent</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600">{selectedPartner.discount_rate_first}%</p>
                      <p className="text-xs text-gray-500">Réduction membre</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Referrals */}
              {partnerStats.recent_referrals?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Filleuls Récents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {partnerStats.recent_referrals.map((ref) => (
                        <div key={ref.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium text-sm">{ref.user_name || "—"}</p>
                            <p className="text-xs text-gray-500">{ref.user_email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {ref.first_transfer_completed ? "1er transfert effectué" : "En attente du 1er transfert"}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(ref.created_at).toLocaleDateString("fr-CA")}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Partner Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le Partenaire</DialogTitle>
          </DialogHeader>

          {selectedPartner && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select
                  value={selectedPartner.status}
                  onValueChange={(v) => {
                    handleStatusChange(selectedPartner.id, v);
                    setSelectedPartner({ ...selectedPartner, status: v });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">Actif</SelectItem>
                    <SelectItem value="suspendu">Suspendu</SelectItem>
                    <SelectItem value="inactif">Inactif</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Commission 1er (%)</Label>
                  <Input
                    type="number"
                    value={selectedPartner.commission_rate_first}
                    onChange={(e) => setSelectedPartner(p => ({ 
                      ...p, 
                      commission_rate_first: parseFloat(e.target.value) || 0 
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Commission récurrente (%)</Label>
                  <Input
                    type="number"
                    value={selectedPartner.commission_rate_recurring}
                    onChange={(e) => setSelectedPartner(p => ({ 
                      ...p, 
                      commission_rate_recurring: parseFloat(e.target.value) || 0 
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Réduction membre (%)</Label>
                  <Input
                    type="number"
                    value={selectedPartner.discount_rate_first}
                    onChange={(e) => setSelectedPartner(p => ({ 
                      ...p, 
                      discount_rate_first: parseFloat(e.target.value) || 0 
                    }))}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Fermer
            </Button>
            <Button
              onClick={async () => {
                try {
                  await axios.put(`${API}/partners/${selectedPartner.id}`, {
                    commission_rate_first: selectedPartner.commission_rate_first,
                    commission_rate_recurring: selectedPartner.commission_rate_recurring,
                    discount_rate_first: selectedPartner.discount_rate_first,
                  });
                  toast.success("Partenaire mis à jour");
                  setShowEditModal(false);
                  fetchPartners();
                } catch (error) {
                  toast.error("Erreur lors de la mise à jour");
                }
              }}
              className="bg-brand-600 hover:bg-brand-700"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerManagement;
